/* ============================================================
   DENZ VISUALISER - HERO + QR REVEAL CONTROLLER
   ============================================================
   - Hero: draggable before/after slider with auto-sweep
   - QR Reveal: user clicks "Reveal My QR" button.
                JS silently opens Roomvo modal (hidden by CSS),
                extracts visitor_id, closes modal, builds QR.
                Real QR appears, scannable.
                Phone scan -> Roomvo upload (linked to desktop).
   ============================================================ */

(function () {
  'use strict';

  function boot() {
    var heroes = document.querySelectorAll('.denz-hero');

    if (heroes.length) {
      heroes.forEach(function (hero) {
        try {
          initHero(hero);
        } catch (err) {
          if (window.console && window.console.warn) {
            window.console.warn('Denz hero init failed:', err);
          }
        }
      });
    }

    // Wire up the reveal button
    initQrReveal();
    initProductGridButtons();
    initHowModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ============================================================
     HERO SLIDER + SWEEP CONTROLLER
     ============================================================ */
  function initHero(hero) {
    var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var slides = hero.querySelectorAll('.denz-hero__slide');
    var dots = hero.querySelectorAll('.denz-hero__dot, .denz-hero__nav-btn');
    var sweepWrap = hero.querySelector('.denz-hero__sweep');
    var handle = hero.querySelector('.denz-hero__divider-handle');
    var scrollCue = hero.querySelector('.denz-hero__scroll-cue');

    var totalSlides = slides.length;
    var currentSlide = 0;
    var sweepDirection = 1;
    var sweepPosition = 50;
    var animationFrame = null;
    var slideTimer = null;
    var isVisible = true;
    var isDragging = false;
    var userHasInteracted = false;
    var autoSweepAllowed = hero.getAttribute('data-sweep-enabled') === 'true' && !prefersReducedMotion;
    var slideDuration = parseInt(hero.getAttribute('data-sweep-speed'), 10) || 4000;

    function setSweepPosition(percent) {
      if (!sweepWrap) return;
      sweepPosition = Math.max(0, Math.min(100, percent));
      sweepWrap.style.setProperty('--denz-sweep-position', sweepPosition + '%');
    }

    function goToSlide(index) {
      if (totalSlides === 0) return;
      currentSlide = (index + totalSlides) % totalSlides;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === currentSlide);
      });
      dots.forEach(function (dot, i) {
        var active = i === currentSlide;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function startSlideTimer() {
      stopSlideTimer();
      if (totalSlides <= 1) return;
      slideTimer = window.setInterval(function () {
        if (isVisible && !isDragging) goToSlide(currentSlide + 1);
      }, slideDuration);
    }

    function stopSlideTimer() {
      if (slideTimer) {
        window.clearInterval(slideTimer);
        slideTimer = null;
      }
    }

    function updatePositionFromEvent(clientX) {
      if (!sweepWrap) return;
      var rect = sweepWrap.getBoundingClientRect();
      var percent = ((clientX - rect.left) / rect.width) * 100;
      setSweepPosition(percent);
    }

    function onDragStart(e) {
      isDragging = true;
      userHasInteracted = true;
      stopAnimation();
      if (sweepWrap) sweepWrap.classList.add('is-dragging');
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updatePositionFromEvent(clientX);
      if (e.preventDefault) e.preventDefault();
    }

    function onDragMove(e) {
      if (!isDragging) return;
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      updatePositionFromEvent(clientX);
      if (e.preventDefault && e.cancelable) e.preventDefault();
    }

    function onDragEnd() {
      if (!isDragging) return;
      isDragging = false;
      if (sweepWrap) sweepWrap.classList.remove('is-dragging');
    }

    if (sweepWrap) {
      sweepWrap.addEventListener('mousedown', onDragStart);
      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', onDragEnd);

      sweepWrap.addEventListener('touchstart', onDragStart, { passive: false });
      window.addEventListener('touchmove', onDragMove, { passive: false });
      window.addEventListener('touchend', onDragEnd);
      window.addEventListener('touchcancel', onDragEnd);

      if (handle) {
        handle.setAttribute('tabindex', '0');
        handle.setAttribute('role', 'slider');
        handle.setAttribute('aria-label', 'Drag to compare before and after');
        handle.setAttribute('aria-valuemin', '0');
        handle.setAttribute('aria-valuemax', '100');
        handle.setAttribute('aria-valuenow', '50');

        handle.addEventListener('keydown', function (e) {
          var step = e.shiftKey ? 10 : 2;
          var newPos = sweepPosition;
          if (e.key === 'ArrowLeft') { newPos = sweepPosition - step; e.preventDefault(); }
          else if (e.key === 'ArrowRight') { newPos = sweepPosition + step; e.preventDefault(); }
          else if (e.key === 'Home') { newPos = 0; e.preventDefault(); }
          else if (e.key === 'End') { newPos = 100; e.preventDefault(); }
          else { return; }
          userHasInteracted = true;
          stopAnimation();
          setSweepPosition(newPos);
          handle.setAttribute('aria-valuenow', Math.round(sweepPosition).toString());
        });
      }
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.stopPropagation();
        var idx = parseInt(dot.getAttribute('data-denz-dot-index'), 10);
        if (!isNaN(idx)) {
          userHasInteracted = true;
          goToSlide(idx);
          startSlideTimer();
        }
      });
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          isVisible = entry.isIntersecting;
          if (isVisible) { startAnimation(); startSlideTimer(); }
          else { stopAnimation(); stopSlideTimer(); }
        });
      }, { threshold: 0.15 });
      io.observe(hero);
    }

    if (scrollCue) {
      scrollCue.style.cursor = 'pointer';
      scrollCue.setAttribute('role', 'button');
      scrollCue.setAttribute('tabindex', '0');
      scrollCue.setAttribute('aria-label', 'Scroll to next section');

      var scrollToNext = function () {
        var heroRect = hero.getBoundingClientRect();
        var heroBottom = window.scrollY + heroRect.bottom;
        window.scrollTo({ top: heroBottom, behavior: 'smooth' });
      };

      scrollCue.addEventListener('click', scrollToNext);
      scrollCue.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          scrollToNext();
        }
      });
    }

    goToSlide(0);
    setSweepPosition(50);
    startSlideTimer();
    if (autoSweepAllowed) {
      startAnimation();
    }
  }

  /* ============================================================
     QR REVEAL: user-triggered silent handoff
     ============================================================ */
  function initQrReveal() {
    var revealBtn = document.querySelector('[data-denz-qr-reveal]');
    if (!revealBtn) return;

    revealBtn.addEventListener('click', function () {
      startSilentQrHandoff(revealBtn);
    });
  }

  function startSilentQrHandoff(revealBtn) {
    var loadingEl = document.querySelector('[data-denz-qr-loading]');

    // Swap reveal button for loading spinner
    revealBtn.hidden = true;
    if (loadingEl) loadingEl.hidden = false;

    // Check Roomvo SDK is ready
    if (typeof roomvo === 'undefined' || !roomvo.startStandaloneVisualizer) {
      waitForRoomvoReady(function () {
        silentlyExtractAndBuildQr(revealBtn, loadingEl);
      });
    } else {
      silentlyExtractAndBuildQr(revealBtn, loadingEl);
    }
  }

  function waitForRoomvoReady(callback) {
    var attempts = 0;
    var maxAttempts = 50;

    var checkInterval = setInterval(function () {
      attempts++;
      if (typeof roomvo !== 'undefined' && roomvo.startStandaloneVisualizer) {
        clearInterval(checkInterval);
        callback();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        if (window.console && window.console.warn) {
          window.console.warn('Roomvo SDK did not load in time');
        }
      }
    }, 200);
  }

  function silentlyExtractAndBuildQr(revealBtn, loadingEl) {
    // Hide Roomvo modal via CSS BEFORE opening
    document.body.classList.add('denz-qr-generating');

    // Open Roomvo silently to spawn the iframe
    try {
      roomvo.startStandaloneVisualizer();
    } catch (err) {
      document.body.classList.remove('denz-qr-generating');
      resetToReveal(revealBtn, loadingEl);
      return;
    }

    // Poll for the visitor_id to appear in the iframe
    var attempts = 0;
    var maxAttempts = 30; // 6 seconds max
    var pollInterval = setInterval(function () {
      attempts++;
      var visitorId = extractRoomvoVisitorId();

      if (visitorId) {
        clearInterval(pollInterval);
        renderHandoffQr(visitorId, loadingEl);
        closeRoomvoModal();

        // Un-hide Roomvo after a tick (so future user clicks open normally)
        setTimeout(function () {
          document.body.classList.remove('denz-qr-generating');
        }, 400);
      } else if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        document.body.classList.remove('denz-qr-generating');
        resetToReveal(revealBtn, loadingEl);
        if (window.console && window.console.warn) {
          window.console.warn('Could not extract Roomvo visitor_id in time');
        }
      }
    }, 200);
  }

  function extractRoomvoVisitorId() {
    var iframes = document.querySelectorAll('iframe');
    for (var i = 0; i < iframes.length; i++) {
      var src = iframes[i].src;
      if (src && src.indexOf('roomvo.com') !== -1) {
        try {
          var iframeUrl = new URL(src);
          var visitorId = iframeUrl.searchParams.get('visitor_id');
          if (visitorId) return visitorId;
        } catch (err) {
          // skip malformed URLs
        }
      }
    }
    return null;
  }

  function renderHandoffQr(visitorId, loadingEl) {
    var mobileUrl = 'https://www.roomvo.com/my/deckocomau/mobile/room-upload/?kiosk_id=&visitor_id=' + encodeURIComponent(visitorId) + '&locale=en-au&is_in_kiosk_mode=0';
    var qrApiUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=' + encodeURIComponent(mobileUrl) + '&bgcolor=ffffff&color=000000&margin=12&format=svg';

    var qrImg = document.querySelector('[data-denz-qr-img]');

    if (qrImg) {
      qrImg.src = qrApiUrl;
      qrImg.hidden = false;
    }
    if (loadingEl) loadingEl.hidden = true;
  }

  function resetToReveal(revealBtn, loadingEl) {
    if (loadingEl) loadingEl.hidden = true;
    if (revealBtn) revealBtn.hidden = false;
  }

  function closeRoomvoModal() {
    // Try common Roomvo close selectors
    var selectors = [
      '[data-roomvo-close]',
      '.roomvo-close',
      '[aria-label*="close" i]',
      '[aria-label*="Close"]'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var btn = document.querySelector(selectors[i]);
      if (btn && btn.offsetParent !== null) {
        btn.click();
        return;
      }
    }

    // Fallback: press Escape key
    try {
      var escEvent = new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, which: 27, bubbles: true });
      document.dispatchEvent(escEvent);
    } catch (err) {
      // older browsers
    }
  }

  /* ============================================================
     PRODUCT GRID - "Try in Visualiser" buttons
     ============================================================ */
  function initProductGridButtons() {
    var buttons = document.querySelectorAll('[data-denz-action="upload"]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (typeof roomvo !== 'undefined' && roomvo.startStandaloneVisualizer) {
          roomvo.startStandaloneVisualizer();
        } else {
          waitForRoomvoReady(function () {
            roomvo.startStandaloneVisualizer();
          });
        }
      });
    });
  }

  function initHowModal() {
    var overlay = document.querySelector('[data-denz-how-modal-overlay]');
    if (!overlay) return;
    var content = overlay.querySelector('[data-denz-how-modal-content]');
    var closeBtn = overlay.querySelector('[data-denz-how-modal-close]');

    function open(mediaEl) {
      var inner = mediaEl.querySelector('.denz-how__media-video, .denz-how__media-gif');
      if (!inner) return;
      content.innerHTML = '';
      var clone = inner.cloneNode(true);
      if (clone.tagName === 'VIDEO') { clone.autoplay = true; clone.loop = true; clone.muted = true; }
      content.appendChild(clone);
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
    }
    function close() {
      overlay.hidden = true;
      content.innerHTML = '';
      document.body.style.overflow = '';
    }

    document.querySelectorAll('[data-denz-how-modal]').forEach(function (btn) {
      btn.addEventListener('click', function () { open(btn); });
    });
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) close(); });
  }
})();